import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AdminRole,
  AdminUser,
  adminUsersApi,
} from '../../../../api/adminUsersApi';
import { RbacRole } from '../../../../api/rolesV2Api';
import {
  colors,
  primaryButtonSx,
  tableCellSx,
  tableHeadCellSx,
  typo,
} from '../../../../theme/tokens';

import AddUserInlineRow from './AddUserInlineRow';
import PendingFirstLoginChip from './PendingFirstLoginChip';
import LastSuperAdminTooltip from './LastSuperAdminTooltip';
import InlineEditCell from './InlineEditCell';
import UserRoleSelectCell from './UserRoleSelectCell';
import { WriteButton, WriteIconButton } from '../../../../components/ui';
import AccessGrantsDrawer from './AccessGrantsDrawer';

interface Props {
  users: AdminUser[];
  /** Legacy role catalog — still needed for `AddUserInlineRow`'s invite
   * dropdown (decision §1: unassigned new users resolve to the v2 Default
   * role regardless of the legacy `role_code` picked here). */
  roles: AdminRole[];
  /** RBAC_V2 role catalog — powers `UserRoleSelectCell`'s single-select. */
  rolesV2: RbacRole[];
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}

function initialsFor(email: string | null | undefined): string {
  if (!email) return '?';
  const local = email.split('@')[0] || email;
  const parts = local.split(/[._-]/).filter(Boolean);
  const picks = parts.length >= 2 ? [parts[0][0], parts[1][0]] : [local[0]];
  return picks.join('').toUpperCase().slice(0, 2);
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

/**
 * Users tab (RBAC_V2 variant) — identical to the legacy `UsersTable` except
 * the Role column: a single-select (`UserRoleSelectCell`, bound to
 * `role_v2_code`) replacing the multi-chip role picker. The "Manage tenant
 * access" drawer is the SAME `AccessGrantsDrawer` the legacy table uses
 * (CPT-1132/1133 merged it with group-derived access already built in — no
 * separate V2 drawer). Every other column/control (email/display-name
 * inline edit, active toggle, invite row, remove/deactivate menu) is
 * unchanged from the legacy table.
 */
const UsersTableV2: React.FC<Props> = ({ users, roles, rolesV2, onNotify }) => {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    user: AdminUser;
  } | null>(null);
  const [accessUser, setAccessUser] = useState<AdminUser | null>(null);

  /**
   * Pre-empt the backend guardrail: if only 1 ACTIVE user holds the v2
   * super-admin role, disable role-change + deactivate on that user. The
   * backend is still authoritative (keyed to the same role, decisions §16).
   */
  const superAdminActiveCount = useMemo(
    () => users.filter((u) => u.role_v2_code === 'super-admin' && u.active).length,
    [users],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(needle) ||
        (u.display_name ?? '').toLowerCase().includes(needle),
    );
  }, [users, search]);

  const updateMutation = useMutation({
    mutationFn: (args: {
      userId: number;
      patch: { role_code?: string; active?: boolean; display_name?: string | null; email?: string };
    }) => adminUsersApi.updateUser(args.userId, args.patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'last_super_admin') {
        onNotify('Cannot remove the last super-admin.', 'error');
      } else if (code === 'email_exists') {
        onNotify('That email is already in use by another user.', 'error');
      } else if (code === 'invalid_email') {
        onNotify('Enter a valid email address.', 'error');
      } else if (code === 'invalid_display_name') {
        onNotify('Display name is too long (max 100 characters).', 'error');
      } else {
        onNotify('Update failed.', 'error');
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => adminUsersApi.removeUser(userId),
    onSuccess: () => {
      onNotify('User removed.', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'last_super_admin') {
        onNotify('Cannot remove the last super-admin.', 'error');
      } else if (code === 'has_activity') {
        onNotify('User has activity — deactivate instead of removing.', 'error');
      } else {
        onNotify('Remove failed.', 'error');
      }
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  const closeAndRun = (fn: () => void) => {
    handleMenuClose();
    fn();
  };

  const copyEmail = (email: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(email).then(
        () => onNotify('Email copied.', 'success'),
        () => onNotify('Copy failed.', 'error'),
      );
    } else {
      onNotify('Clipboard unavailable.', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 320, flex: '1 1 320px' }}
        />
        {!showAdd && (
          <WriteButton ignoreEnvReadOnly
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAdd(true)}
            sx={primaryButtonSx}
          >
            Invite user
          </WriteButton>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>User</TableCell>
                <TableCell sx={tableHeadCellSx}>Display name</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 200 }}>Role</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 96 }}>Active</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 160 }}>Last login</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 64, textAlign: 'center' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && !showAdd && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography sx={{ ...typo.secondaryText }}>
                      No users match the current search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {filtered.map((u) => {
                const isLastSuperAdmin =
                  u.role_v2_code === 'super-admin' && superAdminActiveCount <= 1 && u.active;
                return (
                  <TableRow key={u.user_id} hover>
                    <TableCell sx={tableCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: 12,
                            fontWeight: 700,
                            backgroundColor: colors.adminAccent,
                            color: colors.bgWhite,
                          }}
                        >
                          {initialsFor(u.email)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <InlineEditCell
                            value={u.email}
                            mono
                            label="email"
                            disabled={updateMutation.isPending}
                            confirmMessage={
                              u.first_login_ts != null
                                ? 'This user has already logged in. Changing their email changes how they are identified — they will sign in with the new address. Continue?'
                                : undefined
                            }
                            onSave={(next) =>
                              updateMutation.mutate({ userId: u.user_id, patch: { email: next } })
                            }
                          />
                          {u.first_login_ts == null && (
                            <Box sx={{ mt: 0.5 }}>
                              <PendingFirstLoginChip />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <InlineEditCell
                        value={u.display_name ?? ''}
                        label="display name"
                        disabled={updateMutation.isPending}
                        onSave={(next) =>
                          updateMutation.mutate({
                            userId: u.user_id,
                            patch: { display_name: next.length ? next : null },
                          })
                        }
                      />
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <LastSuperAdminTooltip
                        superAdminCount={
                          u.role_v2_code === 'super-admin' ? superAdminActiveCount : 99
                        }
                        action="demote"
                      >
                        <UserRoleSelectCell
                          userId={u.user_id}
                          currentRoleCode={u.role_v2_code ?? 'default'}
                          roles={rolesV2}
                          onNotify={onNotify}
                          disabled={isLastSuperAdmin}
                        />
                      </LastSuperAdminTooltip>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <LastSuperAdminTooltip
                        superAdminCount={
                          u.role_v2_code === 'super-admin' ? superAdminActiveCount : 99
                        }
                        action="deactivate"
                      >
                        <Switch
                          size="small"
                          checked={u.active}
                          disabled={isLastSuperAdmin || updateMutation.isPending}
                          onChange={(e) =>
                            updateMutation.mutate({
                              userId: u.user_id,
                              patch: { active: e.target.checked },
                            })
                          }
                        />
                      </LastSuperAdminTooltip>
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                      {formatRelative(u.last_login_at)}
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                      <WriteIconButton ignoreEnvReadOnly
                        size="small"
                        aria-label={`Actions — ${u.email}`}
                        onClick={(e) => setMenuAnchor({ el: e.currentTarget, user: u })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </WriteIconButton>
                    </TableCell>
                  </TableRow>
                );
              })}

              {showAdd && (
                <AddUserInlineRow
                  roles={roles}
                  // CPT-1363: invite options must match the Roles tab.
                  rolesV2={rolesV2}
                  onCancel={() => setShowAdd(false)}
                  onCreated={(row) => {
                    setShowAdd(false);
                    onNotify(`Invited ${row.email}`, 'success');
                    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
                    queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
                  }}
                  onError={(msg) => onNotify(msg, 'error')}
                />
              )}

              {!showAdd && users.length > 0 && (
                <TableRow
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setShowAdd(true)}
                  data-write-action="true"
                >
                  <TableCell colSpan={6} sx={{ ...tableCellSx, py: 1, textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        color: colors.adminAccent,
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      <AddIcon fontSize="small" /> Invite user
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={!!menuAnchor}
        onClose={handleMenuClose}
      >
        {menuAnchor && (
          <>
            {menuAnchor.user.active ? (
              <MenuItem
                data-write-action="true"
                onClick={() =>
                  closeAndRun(() =>
                    updateMutation.mutate({
                      userId: menuAnchor.user.user_id,
                      patch: { active: false },
                    }),
                  )
                }
                disabled={
                  menuAnchor.user.role_v2_code === 'super-admin' && superAdminActiveCount <= 1
                }
              >
                Deactivate
              </MenuItem>
            ) : (
              <MenuItem
                data-write-action="true"
                onClick={() =>
                  closeAndRun(() =>
                    updateMutation.mutate({
                      userId: menuAnchor.user.user_id,
                      patch: { active: true },
                    }),
                  )
                }
              >
                Reactivate
              </MenuItem>
            )}
            <MenuItem
              data-write-action="true"
              onClick={() => closeAndRun(() => setAccessUser(menuAnchor.user))}
            >
              Manage tenant access
            </MenuItem>
            <MenuItem
              data-write-action="true"
              onClick={() =>
                closeAndRun(() => {
                  if (
                    window.confirm(
                      `Remove ${menuAnchor.user.email}? This is only allowed for users who never logged in.`,
                    )
                  )
                    removeMutation.mutate(menuAnchor.user.user_id);
                })
              }
              disabled={menuAnchor.user.first_login_ts != null}
              sx={{ color: colors.errorText }}
            >
              Remove…
            </MenuItem>
            <MenuItem onClick={() => closeAndRun(() => copyEmail(menuAnchor.user.email))}>
              <ContentCopyIcon fontSize="small" sx={{ mr: 1, color: colors.textSecondary }} />
              Copy email
            </MenuItem>
          </>
        )}
      </Menu>

      <AccessGrantsDrawer
        open={!!accessUser}
        user={accessUser}
        onClose={() => setAccessUser(null)}
        onNotify={onNotify}
      />
    </Box>
  );
};

export default UsersTableV2;
